/**
 * Saved column-width application for descriptor-driven grids.
 *
 * A column that flexes has no width of its own — it takes a share of whatever
 * the table has left, recomputed on every layout pass. Dragging its edge
 * therefore states a width the grid discards a moment later, which is why a
 * resized column springs back and why the drag looks like it fought the table.
 * A width the user chose outranks the share the type would have given them, so
 * the resize is recorded (user preference
 * `grid.<persistKey>.ct<customerTypeId>.columnWidths`) and
 * re-applied as a fixed width with the flex dropped.
 *
 * Stamped against the descriptor's column set for the same reason the saved
 * order is: a width saved for a column set the backend has since changed is no
 * longer a statement about this grid.
 *
 * Contains:
 * - stampSavedColumnWidths — recorded widths + stamp → the value to persist
 * - readSavedColumnWidths — the saved widths, or null when they no longer apply
 * - applyColumnWidths — pure column-def resize
 */

/**
 * The value to persist for a resize: the widths, plus the stamp of the column
 * set they were chosen against.
 *
 * @param {Object<string, number>} widths - field → chosen pixel width
 * @param {?string} stamp - from columnSetStamp
 * @returns {{widths: Object<string, number>, stamp: ?string}}
 */
export function stampSavedColumnWidths(widths, stamp) {
  return { widths, stamp };
}

/**
 * The saved widths to apply, or null when they must not be applied: they were
 * saved against a different column set.
 *
 * @param {*} saved - the persisted preference value
 * @param {?string} stamp - the current column set's stamp
 * @returns {?Object<string, number>}
 */
export function readSavedColumnWidths(saved, stamp) {
  if (!stamp || !saved || typeof saved !== 'object') return null;
  if (saved.stamp !== stamp || !saved.widths || typeof saved.widths !== 'object') return null;
  return saved.widths;
}

/**
 * Apply saved widths over a column-def array.
 *
 * The chosen width replaces both the column's flex and its floor: a user who
 * drags a column below the minimum its type wanted has said the minimum is
 * wrong for their table, and leaving the floor in place would silently clamp
 * the drag back.
 *
 * @param {Array} columns - GridColDef array
 * @param {?Object<string, number>} widths - field → pixel width, or null for none
 * @returns {Array}
 */
export function applyColumnWidths(columns, widths) {
  if (!widths) return columns;
  const fields = Object.keys(widths);
  if (fields.length === 0) return columns;
  return columns.map(col => {
    const width = widths[col.field];
    if (!width) return col;
    return { ...col, width, flex: undefined, minWidth: Math.min(col.minWidth ?? width, width) };
  });
}
